import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Wallet, TrendingUp, Clock } from 'lucide-react';
import { useGetMyCurrentBalance } from '@/api/balance/balance';

interface UserBalanceCardProps {
  onViewHistory?: () => void;
}

export const UserBalanceCard: React.FC<UserBalanceCardProps> = ({ onViewHistory }) => {
  const { data: balance, isLoading, isError, error } = useGetMyCurrentBalance();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Wallet className='h-5 w-5' />
            Annual Balance
          </CardTitle>
          <CardDescription>Your yearly voucher allowance</CardDescription>
        </CardHeader>
        <CardContent>
          <div className='animate-pulse space-y-2'>
            <div className='h-6 bg-gray-200 rounded w-24'></div>
            <div className='h-4 bg-gray-200 rounded w-32'></div>
            <div className='h-4 bg-gray-200 rounded w-28'></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Wallet className='h-5 w-5' />
            Annual Balance
          </CardTitle>
          <CardDescription>Error loading balance information</CardDescription>
        </CardHeader>
        <CardContent>
          <p className='text-sm text-red-600'>{error instanceof Error ? error.message : 'Failed to load balance'}</p>
        </CardContent>
      </Card>
    );
  }

  if (!balance) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Wallet className='h-5 w-5' />
            Annual Balance
          </CardTitle>
          <CardDescription>No balance information available</CardDescription>
        </CardHeader>
        <CardContent>
          <p className='text-sm text-gray-600'>Your balance information is not available at the moment.</p>
        </CardContent>
      </Card>
    );
  }

  const remainingPercentage =
    balance.remaining_credit && balance.total_credit
      ? (Number(balance.remaining_credit) / Number(balance.total_credit)) * 100
      : 0;

  const getBadgeVariant = (percentage: number) => {
    if (percentage > 50) return 'default';
    if (percentage > 20) return 'secondary';
    return 'destructive';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className='flex items-center gap-2'>
          <Wallet className='h-5 w-5' />
          Annual Balance ({balance.year})
        </CardTitle>
        <CardDescription>Your yearly voucher allowance</CardDescription>
      </CardHeader>
      <CardContent className='space-y-4'>
        <div className='grid grid-cols-2 gap-4'>
          <div className='space-y-1'>
            <p className='text-sm font-medium text-gray-600'>Remaining</p>
            <p className='text-2xl font-bold text-green-600'>€{Number(balance.remaining_credit).toFixed(2)}</p>
          </div>
          <div className='space-y-1'>
            <p className='text-sm font-medium text-gray-600'>Total Annual</p>
            <p className='text-lg font-semibold'>€{Number(balance.total_credit).toFixed(2)}</p>
          </div>
        </div>

        <div className='space-y-2'>
          <div className='flex justify-between items-center'>
            <span className='text-sm font-medium'>Used</span>
            <Badge variant={getBadgeVariant(remainingPercentage)}>€{Number(balance.used_credit).toFixed(2)}</Badge>
          </div>
          <div className='w-full bg-gray-200 rounded-full h-2'>
            <div className='bg-blue-600 h-2 rounded-full' style={{ width: `${100 - remainingPercentage}%` }} />
          </div>
          <p className='text-xs text-gray-500'>{(100 - remainingPercentage).toFixed(1)}% used of annual allowance</p>
        </div>

        <div className='flex justify-between items-center pt-2'>
          <div className='flex items-center gap-1 text-sm text-gray-600'>
            <Clock className='h-4 w-4' />
            <span>Resets annually</span>
          </div>
          {onViewHistory && (
            <Button variant='outline' size='sm' onClick={onViewHistory}>
              <TrendingUp className='h-4 w-4 mr-1' />
              View History
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
